'use client';

import { PlayIcon, BookOpenIcon } from '@heroicons/react/16/solid';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import {
  memo,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ComponentPropsWithoutRef,
} from 'react';

import {
  HeroIcon,
  Image as AtomImage,
  Button,
  Link,
  Typography,
} from '@/components/atoms';
import {
  Card,
  LinkButton,
  PlayAllButton,
  PlaylistImage,
} from '@/components/molecules';
import { VideoPlayer } from '@/components/organisms/VideoPlayer';
import { useMediaController } from '@/lib/hooks/media-controller/useMediaController';
import { getContentTypeRelatedAspectClass } from '@/lib/utils/class-filters';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';
import { cn } from '@/lib/utils/tailwind';

import { WysiwygRenderer } from '../Wysiwyg';
import { getMediaRailCtaKey } from '@/components/organisms/Widget/getMediaRailCtaKey';

import type {
  Artwork,
  GenericClip,
  PersonalPlaylist,
  PlaylistTrack,
} from '@/lib/api';
import type { Chapter } from '@/lib/contexts/media-controller/util';

import { ContentTypeVariations } from '@/components/organisms/CardCarousel/types';

export enum ContentCardVariant {
  BASIC = 'basic',
  DETAILED = 'detailed',
  DETAILED_CTA = 'detailedCTA',
  MEDIA_RAIL = 'mediaRail',
}

export type ContentCardType =
  | (typeof ContentTypeVariations)[keyof typeof ContentTypeVariations]
  | string;

const contentCardVariants = cva('inline-flex flex-col items-start size-fit', {
  variants: {
    variant: {
      [ContentCardVariant.BASIC]: '',
      [ContentCardVariant.DETAILED]: '',
      [ContentCardVariant.DETAILED_CTA]: '',
      [ContentCardVariant.MEDIA_RAIL]: '',
    },
  },
  defaultVariants: {
    variant: ContentCardVariant.BASIC,
  },
});

const baseSizeMap = {
  xl: 390,
  lg: 360,
  md: 266,
  sm: 146,
};

const getCardWidthClass = (
  size: keyof typeof baseSizeMap,
  contentType: ContentCardType,
) => {
  const height = baseSizeMap[size];
  return contentType !== ContentTypeVariations.VIDEO
    ? `w-[${height}px]`
    : `w-[${Math.round(height * (16 / 9))}px]`;
};

const getImageSizeClass = () => 'w-full';

// ─── Discriminated union types ────────────────────────────────────────────────

type StandardContentCardProps = Omit<
  VariantProps<typeof contentCardVariants>,
  'variant'
> &
  ComponentPropsWithoutRef<'div'> & {
    variant?: ContentCardVariant;
    contentType: ContentCardType;
    size?: 'xl' | 'lg' | 'md' | 'sm';
    title: string;
    description?: string;
    imageUrl: string;
    slug: string;
    onClickAction?: () => void;
    episodeIndex?: number;
    rawData?: { id: string; chapters: Chapter[] } | PersonalPlaylist;
  };

type MediaRailContentCardProps = {
  variant: ContentCardVariant.MEDIA_RAIL;
  title: string;
  subtitle?: string;
  poster: Artwork | undefined;
  videoGif: Artwork | undefined;
  videoClip: GenericClip | undefined;
  isExpanded: boolean;
  contentType?: string | null;
  watchNowHref: string;
  onClick?: () => void;
  className?: string;
};

export type ContentCardProps =
  | StandardContentCardProps
  | MediaRailContentCardProps;

export type ContentCardRawData =
  | { id: string; chapters: Chapter[] }
  | PersonalPlaylist;

// ─── MEDIA_RAIL internal component ───────────────────────────────────────────

const stopCardClick = (e: ReactMouseEvent) => e.stopPropagation();

const ContentCardMediaRail = memo(
  ({
    title,
    subtitle,
    poster,
    videoGif,
    videoClip,
    isExpanded,
    contentType,
    watchNowHref,
    onClick,
    className,
  }: MediaRailContentCardProps) => {
    const t = useTranslations();
    const ctaLabel = t(getMediaRailCtaKey(contentType));
    const titleId = useId();
    const [gifFailed, setGifFailed] = useState(false);
    const [videoClipFailed, setVideoClipFailed] = useState(false);
    const [posterFailed, setPosterFailed] = useState(false);
    const videoClipRef = useRef<HTMLVideoElement>(null);
    const showPoster = Boolean(poster?.url) && !posterFailed;
    const rawVideoGifUrl = videoGif?.url;
    const videoGifUrl = isExpanded && !gifFailed ? rawVideoGifUrl : undefined;
    const showClipPreview = Boolean(
      isExpanded && videoClip && !videoClipFailed,
    );
    const showMovingPreview = Boolean(showClipPreview || videoGifUrl);
    const clipPosterUrl = videoClip?.security.image_token
      ? `https://image.mux.com/${videoClip.playback_id}/thumbnail.jpg?token=${videoClip.security.image_token}`
      : undefined;

    useEffect(() => {
      if (!isExpanded || !videoClip || !videoClipRef.current) return;
      const el = videoClipRef.current;
      if (videoClip.security.playback_token) {
        el.setAttribute('playback-token', videoClip.security.playback_token);
      }
      el.setAttribute('playback-id', videoClip.playback_id);
    }, [isExpanded, videoClip]);
    const posterW = poster?.dimensions?.width;
    const posterH = poster?.dimensions?.height;
    const posterRatio = posterW && posterH ? posterW / posterH : 0;
    // object-contain only for images slightly wider than the 3:4 container;
    // landscape images (>= 1:1) fill better with object-cover
    const posterFit =
      posterRatio > 3 / 4 && posterRatio < 1
        ? 'object-contain'
        : 'object-cover';

    useEffect(() => {
      setPosterFailed(false);
    }, [poster?.url]);

    useEffect(() => {
      setGifFailed(false);
      setVideoClipFailed(false);
    }, [rawVideoGifUrl, videoClip?.playback_id]);

    return (
      <div
        aria-labelledby={titleId}
        className={cn(
          'relative flex shrink-0 cursor-pointer flex-col items-start transition-[width,opacity] duration-450 ease-[cubic-bezier(0.25,0.1,0.25,1)] will-change-[width,opacity]',
          isExpanded
            ? 'w-(--expanded-width) opacity-100'
            : 'w-(--poster-width) opacity-60 hover:opacity-85',
          className,
        )}
      >
        <button
          type="button"
          className="absolute inset-0 z-20 cursor-pointer rounded-(--content-card-radius-default) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--focus-ring-color) focus-visible:ring-offset-2"
          onClick={onClick}
          aria-label={title}
          aria-expanded={isExpanded}
        />
        <div
          className={cn(
            'order-last [text-align:var(--content-card-text-alignment)] w-full transition-all duration-450 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
            isExpanded
              ? 'h-0 mt-0 overflow-hidden opacity-0'
              : 'h-auto mt-3 opacity-100',
          )}
        >
          {contentType && (
            <span className="sr-only">
              {t(`CONTENT_TYPE_${contentType.toUpperCase()}`)}
            </span>
          )}
          <h3 id={titleId}>
            <Typography.Text
              as="span"
              variant="label"
              size="md"
              className="block truncate font-bold italic text-(--color-text-primary) uppercase"
            >
              {title}
            </Typography.Text>
          </h3>
          <Typography.Text
            variant="body"
            size="sm"
            className="italic text-(--color-text-secondary)"
          >
            {subtitle}
          </Typography.Text>
        </div>
        <div
          className={cn(
            'relative w-full overflow-hidden rounded-(--content-card-radius-default) transition-[height,box-shadow] duration-450 ease-[cubic-bezier(0.25,0.1,0.25,1)] bg-[#1a1a1a] will-change-[height]',
            isExpanded
              ? 'h-(--expanded-height) shadow-[0_0_0_2px_var(--color-neutral-0),0_20px_60px_rgba(0,0,0,0.6)]'
              : 'h-(--poster-height) shadow-none',
          )}
        >
          <div
            className={cn(
              'absolute inset-0 transition-opacity duration-450 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
              showMovingPreview ? 'opacity-0' : 'opacity-100',
            )}
          >
            {showPoster && (
              <AtomImage
                src={poster!.url!}
                alt=""
                fill
                breakpoints={
                  isExpanded
                    ? [
                        [768, 768],
                        [1920, 900],
                      ]
                    : [
                        [768, 768],
                        [1920, 300],
                      ]
                }
                className={cn('h-full w-full object-top', posterFit)}
                priority={isExpanded}
                onError={() => setPosterFailed(true)}
              />
            )}
          </div>

          <div
            className={cn(
              'absolute inset-0 transition-opacity duration-450 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
              showMovingPreview ? 'opacity-100' : 'opacity-0',
            )}
          >
            {showClipPreview ? (
              <VideoPlayer
                ref={videoClipRef}
                type="mux"
                mode="preview"
                title={title}
                videoUrl=""
                posterImageUrl={clipPosterUrl}
                muted
                autoPlay
                loop
                onFatalError={() => setVideoClipFailed(true)}
              />
            ) : videoGifUrl ? (
              <AtomImage
                src={videoGifUrl}
                alt=""
                fill
                breakpoints={
                  isExpanded
                    ? [
                        [768, 768],
                        [1920, 900],
                      ]
                    : [
                        [768, 768],
                        [1920, 300],
                      ]
                }
                unoptimized
                priority={isExpanded}
                className="h-full w-full object-cover object-top"
                onError={() => setGifFailed(true)}
              />
            ) : null}
          </div>

          <div
            className={cn(
              'absolute inset-0 z-30 flex pointer-events-none transition-opacity duration-300',
              '[align-items:var(--media-rail-cta-alignment)]',
              '[justify-content:var(--media-rail-cta-justification)]',
              'p-(--media-rail-cta-padding)',
              isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <LinkButton
              href={watchNowHref}
              tabIndex={isExpanded ? undefined : -1}
              aria-hidden={isExpanded ? undefined : true}
              className={cn(
                'pointer-events-auto flex items-center justify-center gap-2 p-3 text-[15px] font-medium uppercase text-(--color-neutral-1000) transition-[opacity,transform,background-color,color] duration-300 ease-out rounded-(--media-rail-cta-radius)',
                isExpanded
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-2.5 opacity-0 pointer-events-none',
              )}
              onClick={stopCardClick}
              size="lg"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-(--media-rail-cta-icon-size) h-(--media-rail-cta-icon-size) fill-current"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              {ctaLabel}
            </LinkButton>
          </div>
        </div>
      </div>
    );
  },
);

ContentCardMediaRail.displayName = 'ContentCard.MediaRail';

// ─── Standard card internal component ────────────────────────────────────────

const ContentCardStandard = ({
  variant = ContentCardVariant.BASIC,
  size = 'md',
  className,
  contentType,
  title,
  description,
  imageUrl,
  slug,
  onClickAction,
  episodeIndex,
  rawData,
  ...props
}: StandardContentCardProps) => {
  const t = useTranslations();
  const titleId = useId();
  const { cardHeaderAspectClass } =
    getContentTypeRelatedAspectClass(contentType);
  const contentTypeTranslated =
    contentType !== undefined
      ? t(`CONTENT_TYPE_${contentType.toUpperCase()}`)
      : '';

  const {
    isPlaying,
    currentCompilationType,
    currentCompilationId,
    trackIndex,
  } = useMediaController();

  const canDisplayDescription = (v: ContentCardVariant) =>
    v === ContentCardVariant.DETAILED || v === ContentCardVariant.DETAILED_CTA;

  let effectiveCompilationType = contentType;

  if (contentType === ContentTypeVariations.LIBRARY) {
    if (slug === 'favorite-tracks') {
      effectiveCompilationType = 'personal_favourites';
    } else if (slug === 'recent-tracks') {
      effectiveCompilationType = 'personal_recently_viewed';
    } else {
      effectiveCompilationType = 'personal_playlist';
    }
  }

  const isCurrent =
    effectiveCompilationType === 'personal_favourites' ||
    effectiveCompilationType === 'personal_recently_viewed'
      ? effectiveCompilationType === currentCompilationType
      : effectiveCompilationType === 'personal_playlist'
        ? effectiveCompilationType === currentCompilationType &&
          slug.split('/')[1] === currentCompilationId
        : effectiveCompilationType === currentCompilationType &&
          (effectiveCompilationType === ContentTypeVariations.PODCAST
            ? rawData && 'id' in rawData
              ? rawData.id === currentCompilationId &&
                episodeIndex === trackIndex
              : slug === currentCompilationId
            : slug === currentCompilationId);

  const renderActionButton = () => {
    const isCurrentlyPlaying = isPlaying && isCurrent;
    const playLabel = `${isCurrentlyPlaying ? t('FOCAL_PAUSE') : t('CARD_ACTION_BUTTON_LISTEN')} ${title}`;

    const common = cn(
      'absolute right-(--content-card-action-right) bottom-(--content-card-action-bottom) [transform:var(--content-card-action-transform)] z-40 min-h-6 min-w-6 rounded-(--button-primary-radius-default)',
      isCurrentlyPlaying
        ? 'flex pointer-events-auto'
        : 'pointer-fine:flex pointer-fine:pointer-events-auto pointer-coarse:sr-only pointer-coarse:focus-visible:not-sr-only',
    );

    switch (contentType) {
      case ContentTypeVariations.ALBUM:
      case ContentTypeVariations.PLAYLIST:
      case ContentTypeVariations.LIBRARY:
        if (
          contentType === ContentTypeVariations.LIBRARY &&
          effectiveCompilationType === 'personal_playlist' &&
          rawData &&
          (('playLists' in rawData &&
            (!rawData.playLists ||
              !Array.isArray(rawData.playLists) ||
              rawData.playLists.length === 0)) ||
            ('tracks' in rawData &&
              (!rawData.tracks ||
                !Array.isArray(rawData.tracks) ||
                rawData.tracks.length === 0)))
        ) {
          return null;
        }

        return (
          <PlayAllButton
            mediaType={
              contentType === ContentTypeVariations.LIBRARY
                ? slug === 'favorite-tracks'
                  ? ContentTypeVariations.FAVORITE_PLAYLIST
                  : slug === 'recent-tracks'
                    ? ContentTypeVariations.RECENT_PLAYLIST
                    : ContentTypeVariations.PERSONAL_PLAYLIST
                : contentType
            }
            id={slug}
            rawData={
              effectiveCompilationType === 'personal_playlist' &&
              rawData &&
              'playListId' in rawData
                ? rawData
                : undefined
            }
            aria-label={playLabel}
            variant="primary"
            size="sm"
            className={common}
            episodeIndex={episodeIndex}
          >
            {isPlaying && isCurrent
              ? t('FOCAL_PAUSE')
              : t('CARD_ACTION_BUTTON_LISTEN')}
          </PlayAllButton>
        );
      case ContentTypeVariations.PODCAST:
        return (
          <PlayAllButton
            mediaType={ContentTypeVariations.PODCAST}
            id={slug}
            rawData={rawData}
            episodeIndex={episodeIndex}
            aria-label={playLabel}
            variant="primary"
            size="sm"
            className={common}
          >
            {isPlaying && isCurrent
              ? t('FOCAL_PAUSE')
              : t('CARD_ACTION_BUTTON_LISTEN')}
          </PlayAllButton>
        );
      case ContentTypeVariations.ARTICLE:
        return (
          <LinkButton
            href={`/${contentType}/${slug}`}
            variant="primary"
            size="sm"
            className={common}
            onClick={stopCardClick}
            aria-label={`${t('CARD_ACTION_BUTTON_READ')} ${title}`}
          >
            <HeroIcon icon={BookOpenIcon} size="medium" />
            {t('CARD_ACTION_BUTTON_READ')}
          </LinkButton>
        );
      case ContentTypeVariations.COLLECTION:
      case ContentTypeVariations.GALLERY:
        return (
          <LinkButton
            href={`/${contentType}/${slug}`}
            variant="primary"
            size="sm"
            className={common}
            onClick={stopCardClick}
            aria-label={`${t('CARD_ACTION_BUTTON_EXPLORE')} ${title}`}
          >
            <HeroIcon icon={PlayIcon} size="medium" />
            {t('CARD_ACTION_BUTTON_EXPLORE')}
          </LinkButton>
        );
      case ContentTypeVariations.VIDEO:
      default:
        return (
          <LinkButton
            href={`/${contentType}/${slug}`}
            variant="primary"
            size="sm"
            className={common}
            onClick={stopCardClick}
            aria-label={`${t('CARD_ACTION_BUTTON_WATCH')} ${title}`}
          >
            <HeroIcon icon={PlayIcon} size="medium" />
            {t('CARD_ACTION_BUTTON_WATCH')}
          </LinkButton>
        );
    }
  };

  const isCurrentCardPlaying =
    isPlaying &&
    (contentType === ContentTypeVariations.PODCAST
      ? rawData && 'id' in rawData
        ? rawData.id === currentCompilationId && episodeIndex === trackIndex
        : slug === currentCompilationId
      : contentType === ContentTypeVariations.LIBRARY &&
          effectiveCompilationType === 'personal_playlist'
        ? slug.split('/')[1] === currentCompilationId
        : effectiveCompilationType === 'personal_favourites' ||
            effectiveCompilationType === 'personal_recently_viewed'
          ? effectiveCompilationType === currentCompilationType
          : slug === currentCompilationId);

  const isWideAspect =
    contentType === ContentTypeVariations.VIDEO ||
    contentType === ContentTypeVariations.COLLECTION;

  const detailsHref =
    contentType === ContentTypeVariations.PODCAST &&
    rawData &&
    'id' in rawData &&
    rawData.id
      ? `/${contentType}/${rawData.id}`
      : `/${contentType}/${slug}`;

  return (
    <Card.Root
      aria-labelledby={titleId}
      className={cn(
        contentCardVariants({ variant }),
        'cursor-pointer',
        'p-2',
        getCardWidthClass(size, contentType),
        cardHeaderAspectClass,
        className,
      )}
      {...props}
    >
      <Card.Body className="[align-items:var(--content-card-copy-alignment)] [text-align:var(--content-card-text-alignment)] flex w-full flex-col pt-(--vertical-spacing-2)">
        {contentType !== ContentTypeVariations.LIBRARY && (
          <Typography.Text
            variant="label"
            size="md"
            className={cn(
              'text-(--content-card-label-color-default) transition-colors duration-150 group-hover:text-(--content-card-label-color-hover)',
            )}
          >
            {contentTypeTranslated}
          </Typography.Text>
        )}
        <Typography.Heading
          variant="h3"
          className={cn(
            'mb-0 text-pretty font-(--font-body) text-(length:--text-body--line-height)',
            'text-(length:--text-body-md) font-(--font-weight-body-md) normal-case not-italic',
          )}
        >
          <Link
            href={detailsHref}
            className={cn(
              'no-underline! hover:no-underline! text-(--color-text-primary)',
              'focus-visible:outline-none focus-visible:[&>span:first-child]:ring-2 focus-visible:[&>span:first-child]:ring-(--focus-ring-color) focus-visible:[&>span:first-child]:ring-offset-2',
            )}
            aria-label={t('VIEW_DETAILS_FOR') + ` ${title}`}
          >
            <span
              aria-hidden="true"
              className="absolute inset-0 z-30 rounded-(--content-card-radius-default)"
            />
            <span id={titleId} className="line-clamp-2">
              {title}
            </span>
          </Link>
        </Typography.Heading>
        {canDisplayDescription(variant) && description?.trim() && (
          <>
            {/<\s*[a-z][\s\S]*>/i.test(description) ? (
              <div className="line-clamp-2">
                <WysiwygRenderer html={description} />
              </div>
            ) : (
              <Typography.Text
                variant="paragraph"
                size="md"
                className="line-clamp-2"
              >
                {description}
              </Typography.Text>
            )}
          </>
        )}
      </Card.Body>
      <Card.Header
        className={cn(
          'order-first overflow-hidden rounded-(--content-card-radius-default) flex flex-col-reverse group',
          'outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-(--focus-ring-color) focus-visible:ring-offset-2',
          cardHeaderAspectClass,
          getImageSizeClass(),
        )}
      >
        {effectiveCompilationType === 'personal_playlist' &&
        rawData &&
        (('playLists' in rawData && rawData.playLists) ||
          ('tracks' in rawData && rawData.tracks)) ? (
          <div className="relative z-30 group pointer-events-none rounded-(--content-card-radius-default) w-full h-full shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:opacity-90">
            <PlaylistImage
              images={(
                ('playLists' in rawData
                  ? rawData.playLists
                  : (rawData as { tracks: PlaylistTrack[] }).tracks) || []
              ).map((track: PlaylistTrack) => ({
                src: track.track_metadata?.album?.pictureUrl ?? '',
                title: track.track_metadata?.album?.title ?? '',
              }))}
              responsive={true}
            />
            <div
              className={cn(
                '[background:var(--content-card-overlay-background)] absolute inset-0 z-40 pointer-events-none transition-opacity duration-300 rounded-(--content-card-radius-default)',
                isCurrentCardPlaying
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-has-focus-visible:opacity-100',
              )}
            >
              {renderActionButton()}
            </div>
          </div>
        ) : (
          <AtomImage
            src={
              imageUrl ||
              getRandomDefaultImage(isWideAspect ? 'video' : 'square')
            }
            alt=""
            aspectRatio={isWideAspect ? 'wide' : 'square'}
            aria-hidden={true}
            className="rounded-(--content-card-radius-default) w-full h-full shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:opacity-90"
            wrapperClassName="group"
          >
            <div
              className={cn(
                '[background:var(--content-card-overlay-background)] absolute inset-0 z-40 pointer-events-none transition-opacity duration-300',
                isCurrentCardPlaying || (isPlaying && isCurrent)
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 group-has-focus-visible:opacity-100',
              )}
            >
              {renderActionButton()}
            </div>
          </AtomImage>
        )}
      </Card.Header>
      {variant === ContentCardVariant.DETAILED_CTA && (
        <Card.Footer className="relative z-50">
          <Button
            type="button"
            aria-label={`${t('CARD_FOOTER_BUTTON')} ${title}`}
            onClick={e => {
              e.stopPropagation();
              onClickAction?.();
            }}
          >
            {t('CARD_FOOTER_BUTTON')}
          </Button>
        </Card.Footer>
      )}
    </Card.Root>
  );
};

// ─── Public export ────────────────────────────────────────────────────────────

export const ContentCard = (props: ContentCardProps) => {
  if ('isExpanded' in props) {
    return <ContentCardMediaRail {...props} />;
  }
  return <ContentCardStandard {...props} />;
};
